// A landing from an ad (the park office's Marketing screen gives the campaign its target URL):
// ?ad=propellerads&subid=…&campaign=…, read once at boot and taken out of the address bar, like an
// invite. The api records the click and hands back an opaque ref; the phone keeps it and sends it
// when it makes its guest, so the new player is the click's. A player who already has an account
// is never claimed by a click. Standalone only: inside the Lagos Life frame there are no ads of ours.
import { api } from './api';

const KEY = 'wahala.adRef';
/** The server holds the real window (the office sets it); the phone stops offering after 90 days. */
const KEEP_MS = 90 * 86_400_000;
/** Our landing's parameter for each macro the network fills in. */
const FIELDS = {
  campaign: 'campaignId',
  zone: 'zoneId',
  banner: 'bannerId',
  country: 'country',
  os: 'os',
  device: 'device',
  browser: 'browser',
  cost: 'cost',
} as const;

/** Called once at boot: record an ad landing and drop its parameters from the URL. */
export async function takeAdClick(): Promise<void> {
  const q = new URLSearchParams(location.search);
  if (!q.has('ad')) return;
  const network = q.get('ad');
  const clickId = q.get('subid');
  const body: Record<string, string> = {};
  for (const [param, field] of Object.entries(FIELDS)) {
    const v = q.get(param);
    if (v) body[field] = v;
    q.delete(param);
  }
  q.delete('ad');
  q.delete('subid');
  const rest = q.toString();
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  if (network !== 'propellerads' || !clickId) return;
  try {
    const { ref } = await api.adClick({ network, clickId, ...body });
    localStorage.setItem(KEY, JSON.stringify({ ref, at: Date.now() }));
  } catch {
    // a refused click, or storage blocked: the player plays all the same
  }
}

/** The kept click's ref, for a new guest; null when there is none or it is too old. */
export function adRef(): string | null {
  try {
    const kept = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { ref?: unknown; at?: unknown } | null;
    if (typeof kept?.ref !== 'string' || typeof kept.at !== 'number' || Date.now() - kept.at > KEEP_MS) return null;
    return kept.ref;
  } catch {
    return null;
  }
}

/** The guest is made: the click has done its work. */
export function forgetAdRef(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // storage blocked
  }
}
